import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Title,
  Grid,
  Paper,
  Text,
  Group,
  Badge,
  Table,
  ScrollArea,
  Tabs,
  Switch,
  TextInput,
  Loader,
  Center,
  Alert,
  Pagination,
  Select,
  Button,
  Textarea,
  Stack,
  Modal,
  ActionIcon,
} from "@mantine/core";
import { showNotification, updateNotification } from "@mantine/notifications";
import { DatePickerInput } from "@mantine/dates";
import { useDebouncedValue } from "@mantine/hooks";
import dayjs from "dayjs";
import {
  IconMessageCircle,
  IconAlertTriangle,
  IconBrandWhatsapp,
  IconSettings,
  IconChartBar,
  IconList,
  IconUsers,
  IconBrush,
  IconHelp,
  IconActivity,
  IconDatabase,
  IconPencil,
} from "@tabler/icons-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { useResibotApi } from "../../../hooks/useResibotApi";

function buildDailyChartSeries(days, points) {
  const byDate = new Map((points || []).map((p) => [p.date, p]));
  const out = [];
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = dayjs().subtract(i, "day").format("YYYY-MM-DD");
    const row = byDate.get(d);
    out.push({
      date: d,
      label: dayjs(d).format("MMM D"),
      queries: row?.queries ?? 0,
      fallbacks: row?.fallbacks ?? 0,
    });
  }
  return out;
}

const CLEANING_STATUS_SELECT_DATA = [
  { value: "pending", label: "Pending" },
  { value: "done", label: "Completed" },
  { value: "overdue", label: "Overdue" },
];

function cleaningStatusDisplay(status) {
  const s = String(status || "").toLowerCase();
  if (s === "pending") return { label: "Pending", color: "yellow" };
  if (s === "overdue") return { label: "Overdue", color: "red" };
  if (s === "done") return { label: "Completed", color: "green" };
  return { label: status || "—", color: "gray" };
}

function normalizeCleaningRowStatus(status) {
  const s = String(status || "").toLowerCase();
  if (s === "pending" || s === "done" || s === "overdue") return s;
  return "pending";
}

function StatCard({ title, value, icon: Icon, color, subtitle }) {
  return (
    <Paper shadow="sm" p="md" radius="md" withBorder style={{ height: "100%" }}>
      <Group position="apart" noWrap>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text size="xs" color="dimmed" transform="uppercase" weight={700} lineClamp={1}>
            {title}
          </Text>
          <Text size={28} weight={700} mt={2} style={{ lineHeight: 1.2 }}>
            {value}
          </Text>
          {subtitle && (
            <Text size="xs" color="dimmed" mt={6} lineClamp={2}>
              {subtitle}
            </Text>
          )}
        </div>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            backgroundColor: `${color || "#4a8078"}14`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={24} stroke={1.5} color={color || "#4a8078"} />
        </div>
      </Group>
    </Paper>
  );
}

function humanError(err) {
  if (!err?.message) return "Something went wrong.";
  const s = err.status;
  if (s === 401) {
    return "Sign-in expired or access was denied. Please sign in again.";
  }
  if (s === 403) {
    return "You don't have permission to view this.";
  }
  if (s === 422) {
    return "Invalid request. Please check your filters and try again.";
  }
  if (s === 500) {
    return "Something went wrong. Please try again.";
  }
  if (s === 503) {
    return "The service is temporarily unavailable. Please try again later.";
  }
  return err.message;
}

function OverviewTab({ request }) {
  const [rangeDays, setRangeDays] = useState("30");
  const [stats, setStats] = useState(null);
  const [dailyPoints, setDailyPoints] = useState([]);
  const [recentQueries, setRecentQueries] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const daysNum = Number(rangeDays) || 30;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const qs = (params) =>
      new URLSearchParams(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== "")
      ).toString();

    Promise.all([
      request(`/api/analytics/summary?${qs({ days: daysNum })}`),
      request(`/api/analytics/daily?${qs({ days: daysNum })}`),
      request(`/api/analytics/recent?${qs({ limit: 10, days: daysNum })}`),
    ])
      .then(([summary, daily, recent]) => {
        if (cancelled) return;
        setStats({
          totalQueries: summary?.total_queries ?? 0,
          fallbackRate: summary?.fallback_rate ?? 0,
          avgResponseMs: summary?.avg_response_time_ms ?? 0,
          fallbackCount: summary?.fallback_count ?? 0,
          whatsappContacts: summary?.contacts_count ?? 0,
        });
        setDailyPoints(daily?.points || []);
        setRecentQueries(recent?.queries || []);
      })
      .catch((e) => {
        if (!cancelled) setError(humanError(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [request, daysNum]);

  const lineData = buildDailyChartSeries(daysNum, dailyPoints);
  const answeredCount = Math.max(
    0,
    (stats?.totalQueries ?? 0) - (stats?.fallbackCount ?? 0)
  );
  const donutData = [
    { name: "Answered (no fallback)", value: answeredCount, color: "#4a8078" },
    { name: "Fallback", value: stats?.fallbackCount ?? 0, color: "#e03131" },
  ].filter((d) => d.value > 0);

  if (loading) {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <>
      {error && (
        <Alert color="red" title="Could not load overview" mb="md" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Group position="apart" align="center" mb="md">
        <Text size="sm" weight={600} color="dimmed">Key metrics</Text>
        <Select
          size="xs"
          w={160}
          data={[
            { value: "7", label: "Last 7 days" },
            { value: "30", label: "Last 30 days" },
            { value: "90", label: "Last 90 days" },
          ]}
          value={rangeDays}
          onChange={(v) => setRangeDays(v || "30")}
          styles={{ input: { fontWeight: 500 } }}
        />
      </Group>
      <Grid mb="md" gutter="md">
        <Grid.Col xs={12} sm={6} md={3}>
          <StatCard
            title="Queries answered"
            value={stats?.totalQueries ?? 0}
            icon={IconMessageCircle}
            color="#4a8078"
            subtitle={`Rolling ${daysNum} days`}
          />
        </Grid.Col>
        <Grid.Col xs={12} sm={6} md={3}>
          <StatCard
            title="Fallback rate"
            value={`${(stats?.fallbackRate ?? 0).toFixed(1)}%`}
            icon={IconAlertTriangle}
            color={stats?.fallbackRate > 20 ? "#e03131" : "#f59f00"}
            subtitle={`Rolling ${daysNum} days`}
          />
        </Grid.Col>
        <Grid.Col xs={12} sm={6} md={3}>
          <StatCard
            title="Avg response time"
            value={
              stats?.avgResponseMs != null
                ? `${Math.round(stats.avgResponseMs)} ms`
                : "—"
            }
            icon={IconActivity}
            color="#228be6"
            subtitle={`Rolling ${daysNum} days`}
          />
        </Grid.Col>
        <Grid.Col xs={12} sm={6} md={3}>
          <StatCard
            title="WhatsApp contacts"
            value={stats?.whatsappContacts ?? 0}
            icon={IconBrandWhatsapp}
            color="#fd7e14"
            subtitle="Guests redirected to WhatsApp"
          />
        </Grid.Col>
      </Grid>

      <Grid mb="md" gutter="md">
        <Grid.Col xs={12} md={8}>
          <Paper shadow="sm" p="md" radius="md" withBorder h={340}>
            <Text size="sm" weight={600} mb="md">
              Queries per day
            </Text>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={lineData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={40} />
                <RechartsTooltip />
                <Line type="monotone" dataKey="queries" name="Queries" stroke="#4a8078" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid.Col>
        <Grid.Col xs={12} md={4}>
          <Paper shadow="sm" p="md" radius="md" withBorder h={340}>
            <Text size="sm" weight={600} mb="xs">
              Answered vs fallback
            </Text>
            {(stats?.totalQueries ?? 0) === 0 ? (
              <Center h={280}>
                <Text color="dimmed" size="sm">
                  No queries in this period
                </Text>
              </Center>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={donutData.length ? donutData : [{ name: "No data", value: 1, color: "#dee2e6" }]}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={58}
                    outerRadius={88}
                    paddingAngle={2}
                  >
                    {(donutData.length ? donutData : [{ color: "#dee2e6" }]).map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid.Col>
      </Grid>

      <Paper shadow="sm" p="md" radius="md" withBorder>
        <Group position="apart" mb="sm">
          <Title order={4}>Recent Queries</Title>
          <Badge variant="light" color="gray" size="sm">{recentQueries.length} shown</Badge>
        </Group>
        <ScrollArea h={380}>
          <Table striped highlightOnHover>
            <thead>
              <tr>
                <th>Guest / query</th>
                <th>Response</th>
                <th>Fallback</th>
                <th>Time</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {recentQueries.map((q) => (
                <tr key={q.id || q.session_id + (q.query_timestamp || "")}>
                  <td style={{ maxWidth: 220 }}>
                    <Stack spacing={4}>
                      {(q.visitor_name || q.visitor_email) && (
                        <Text size="xs" weight={600} color="dimmed">
                          {[q.visitor_name, q.visitor_email].filter(Boolean).join(" · ")}
                        </Text>
                      )}
                      <Text size="sm" lineClamp={2}>
                        {q.query_message || q.user_query}
                      </Text>
                    </Stack>
                  </td>
                  <td style={{ maxWidth: 250 }}>
                    <Text size="sm" lineClamp={2}>
                      {q.bot_response}
                    </Text>
                  </td>
                  <td>
                    <Badge color={q.fallback_triggered ? "red" : "green"} size="sm">
                      {q.fallback_triggered ? "Yes" : "No"}
                    </Badge>
                  </td>
                  <td>
                    <Text size="sm">{q.response_time_ms}ms</Text>
                  </td>
                  <td>
                    <Text size="xs" color="dimmed">
                      {q.query_timestamp
                        ? new Date(q.query_timestamp).toLocaleDateString()
                        : "-"}
                    </Text>
                  </td>
                </tr>
              ))}
              {recentQueries.length === 0 && (
                <tr>
                  <td colSpan={5}>
                    <Text color="dimmed" align="center">
                      No queries yet
                    </Text>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </ScrollArea>
      </Paper>
    </>
  );
}

function QueriesTab({ request, tenantId }) {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState("");
  const [debounced] = useDebouncedValue(search, 400);
  const [fallbackOnly, setFallbackOnly] = useState(false);
  const [timeMode, setTimeMode] = useState("90");
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);
  const [queries, setQueries] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      tenant_id: tenantId,
    });
    if (debounced) qs.set("search", debounced);
    if (fallbackOnly) qs.set("fallback_only", "true");

    if (timeMode === "custom") {
      if (customFrom) qs.set("date_from", dayjs(customFrom).format("YYYY-MM-DD"));
      if (customTo) qs.set("date_to", dayjs(customTo).format("YYYY-MM-DD"));
    } else if (timeMode !== "all") {
      const days = Number(timeMode);
      qs.set(
        "date_from",
        dayjs().subtract(days, "day").format("YYYY-MM-DD")
      );
    }

    try {
      const data = await request(`/api/analytics/queries?${qs.toString()}`);
      setQueries(data.queries || []);
      setTotal(data.total ?? 0);
      const p = data.pages;
      setPages(typeof p === "number" ? p : 0);
    } catch (e) {
      setError(humanError(e));
      setQueries([]);
      setPages(0);
    } finally {
      setLoading(false);
    }
  }, [
    request,
    tenantId,
    page,
    limit,
    debounced,
    fallbackOnly,
    timeMode,
    customFrom,
    customTo,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Paper shadow="sm" p="lg" radius="md" withBorder>
      {error && (
        <Alert color="red" mb="md" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Group mb="md" grow align="flex-end">
        <TextInput
          label="Search queries"
          placeholder="Substring match on user question…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <Select
          label="Time window"
          data={[
            { value: "30", label: "Last 30 days" },
            { value: "90", label: "Last 90 days" },
            { value: "365", label: "Last 365 days" },
            { value: "all", label: "All time" },
            { value: "custom", label: "Custom range" },
          ]}
          value={timeMode}
          onChange={(v) => {
            setTimeMode(v || "90");
            setPage(1);
          }}
        />
        <Select
          label="Filter"
          data={[
            { value: "all", label: "All queries" },
            { value: "fallback", label: "Fallback only" },
          ]}
          value={fallbackOnly ? "fallback" : "all"}
          onChange={(v) => {
            setFallbackOnly(v === "fallback");
            setPage(1);
          }}
        />
      </Group>
      {timeMode === "custom" && (
        <Group mb="md" grow>
          <DatePickerInput
            label="From"
            value={customFrom}
            onChange={(d) => {
              setCustomFrom(d);
              setPage(1);
            }}
            maxDate={customTo || undefined}
          />
          <DatePickerInput
            label="To"
            value={customTo}
            onChange={(d) => {
              setCustomTo(d);
              setPage(1);
            }}
            minDate={customFrom || undefined}
          />
        </Group>
      )}
      <Text size="sm" color="dimmed" mb="sm">
        Total matching: {total}
      </Text>
      {loading ? (
        <Center p="xl">
          <Loader />
        </Center>
      ) : (
        <>
          <ScrollArea h={520}>
            <Table striped highlightOnHover>
              <thead>
                <tr>
                  <th>Guest</th>
                  <th>Query</th>
                  <th>Response</th>
                  <th>Fallback</th>
                  <th>Time (ms)</th>
                  <th>Model</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {queries.map((q) => (
                  <tr key={q.id}>
                    <td style={{ maxWidth: 120 }}>
                      <Text size="xs" lineClamp={2}>
                        {q.visitor_name || q.visitor_email || "—"}
                      </Text>
                    </td>
                    <td style={{ maxWidth: 160 }}>
                      <Text size="sm" lineClamp={2}>
                        {q.query_message || q.user_query}
                      </Text>
                    </td>
                    <td style={{ maxWidth: 220 }}>
                      <Text size="sm" lineClamp={2}>
                        {q.bot_response}
                      </Text>
                    </td>
                    <td>
                      <Badge color={q.fallback_triggered ? "red" : "green"} size="sm">
                        {q.fallback_triggered ? "Yes" : "No"}
                      </Badge>
                    </td>
                    <td>
                      <Text size="sm">{q.response_time_ms}</Text>
                    </td>
                    <td>
                      <Text size="xs">{q.llm_model || "-"}</Text>
                    </td>
                    <td>
                      <Text size="xs" color="dimmed">
                        {q.query_timestamp
                          ? new Date(q.query_timestamp).toLocaleString()
                          : "-"}
                      </Text>
                    </td>
                  </tr>
                ))}
                {queries.length === 0 && !loading && (
                  <tr>
                    <td colSpan={7}>
                      <Text color="dimmed" align="center" py="md">
                        No queries match these filters.
                      </Text>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </ScrollArea>
          {total > 0 && pages > 1 ? (
            <Group position="center" mt="md">
              <Pagination value={page} onChange={setPage} total={pages} />
            </Group>
          ) : null}
        </>
      )}
    </Paper>
  );
}

function FaqsTab({ request, tenantId }) {
  const [topFaqs, setTopFaqs] = useState([]);
  const [unanswered, setUnanswered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeMode, setTimeMode] = useState("90");
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);

  const dateFrom = (() => {
    if (timeMode === "custom") {
      return customFrom ? dayjs(customFrom).format("YYYY-MM-DD") : undefined;
    }
    if (timeMode === "all") return undefined;
    const days = Number(timeMode) || 90;
    return dayjs().subtract(days, "day").format("YYYY-MM-DD");
  })();

  const dateTo =
    timeMode === "custom" && customTo
      ? dayjs(customTo).format("YYYY-MM-DD")
      : undefined;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({
      tenant_id: tenantId,
      limit: "25",
    });
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);

    request(`/api/analytics/faqs?${params.toString()}`)
      .then((data) => {
        if (!cancelled) {
          setTopFaqs(data.top_faqs ?? []);
          setUnanswered(data.unanswered ?? []);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(humanError(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [request, tenantId, dateFrom, dateTo]);

  return (
    <Grid>
      {error && (
        <Grid.Col span={12}>
          <Alert color="red" mb="md" withCloseButton onClose={() => setError(null)}>
            {error}
          </Alert>
        </Grid.Col>
      )}

      <Grid.Col span={12}>
        <Group position="apart" align="center" mb="sm">
          <Text size="sm" weight={600} color="dimmed">Question themes</Text>
          <Group spacing="sm" align="flex-end">
            <Select
              size="xs"
              w={160}
              data={[
                { value: "30", label: "Last 30 days" },
                { value: "90", label: "Last 90 days" },
                { value: "365", label: "Last 365 days" },
                { value: "all", label: "All time" },
                { value: "custom", label: "Custom range" },
              ]}
              value={timeMode}
              onChange={(v) => setTimeMode(v || "90")}
              styles={{ input: { fontWeight: 500 } }}
            />
            {timeMode === "custom" && (
              <>
                <DatePickerInput
                  size="xs"
                  label="From"
                  value={customFrom}
                  onChange={setCustomFrom}
                  maxDate={customTo || undefined}
                  w={140}
                />
                <DatePickerInput
                  size="xs"
                  label="To"
                  value={customTo}
                  onChange={setCustomTo}
                  minDate={customFrom || undefined}
                  w={140}
                />
              </>
            )}
          </Group>
        </Group>
      </Grid.Col>

      <Grid.Col xs={12} md={6} style={{ display: "flex" }}>
        <Paper shadow="sm" p="lg" radius="md" withBorder style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Title order={4} mb="md">
            Most Asked Questions
          </Title>
          {loading ? (
            <Center style={{ flex: 1 }}>
              <Loader />
            </Center>
          ) : (
            <ScrollArea style={{ flex: 1, minHeight: 300 }}>
              <Table striped highlightOnHover>
                <thead>
                  <tr>
                    <th>Question theme</th>
                    <th>Count</th>
                    <th>Fallbacks</th>
                  </tr>
                </thead>
                <tbody>
                  {topFaqs.map((row, i) => (
                    <tr key={`top-${i}-${row.question}`}>
                      <td>
                        <Text size="sm" lineClamp={3}>
                          {row.question}
                        </Text>
                      </td>
                      <td>{row.count}</td>
                      <td>
                        <Badge color={row.fallback_count > 0 ? "orange" : "green"}>
                          {row.fallback_count}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                  {topFaqs.length === 0 && (
                    <tr>
                      <td colSpan={3}>
                        <Text color="dimmed" align="center" py="md">
                          No questions recorded in this period.
                        </Text>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </ScrollArea>
          )}
        </Paper>
      </Grid.Col>

      <Grid.Col xs={12} md={6} style={{ display: "flex" }}>
        <Paper shadow="sm" p="lg" radius="md" withBorder style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Title order={4} mb="md">
            Unanswered Questions
          </Title>
          <Text size="xs" color="dimmed" mb="sm">
            Questions where the bot could not answer — prioritize adding this to your knowledge base.
          </Text>
          {loading ? (
            <Center style={{ flex: 1 }}>
              <Loader />
            </Center>
          ) : (
            <ScrollArea style={{ flex: 1, minHeight: 300 }}>
              <Table striped>
                <thead>
                  <tr>
                    <th>Question theme</th>
                    <th>Times asked</th>
                    <th>Unanswered</th>
                  </tr>
                </thead>
                <tbody>
                  {unanswered.map((row, i) => (
                    <tr key={`un-${i}-${row.question}`}>
                      <td>
                        <Text size="sm" lineClamp={3}>
                          {row.question}
                        </Text>
                      </td>
                      <td>{row.count}</td>
                      <td>
                        <Badge color="red" variant="light">
                          {row.fallback_count}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                  {unanswered.length === 0 && (
                    <tr>
                      <td colSpan={3}>
                        <Text color="teal" align="center" py="md">
                          Great! No unanswered questions in this period.
                        </Text>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </ScrollArea>
          )}
        </Paper>
      </Grid.Col>
    </Grid>
  );
}

function ContactsTab({ request, tenantId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    request(`/api/analytics/contacts?tenant_id=${encodeURIComponent(tenantId)}&limit=100`)
      .then((data) => {
        if (!cancelled) setRows(data.contacts || []);
      })
      .catch((e) => {
        if (!cancelled) setError(humanError(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [request, tenantId]);

  if (loading) {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Paper shadow="sm" p="lg" radius="md" withBorder>
      {error && (
        <Alert color="red" mb="md">
          {error}
        </Alert>
      )}
      <Title order={4} mb="md">
        WhatsApp / fallback contacts
      </Title>
      <Text size="sm" color="dimmed" mb="sm">
        When the widget collects a lead, guest name and email are parsed from the query log.
      </Text>
      <ScrollArea h={560}>
        <Table striped highlightOnHover>
          <thead>
            <tr>
              <th>Guest</th>
              <th>Email</th>
              <th>Query</th>
              <th>Session</th>
              <th>Repeat</th>
              <th>Count</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c, idx) => (
              <tr
                key={c.id || `${c.session_id || "s"}-${idx}-${c.query_timestamp || ""}`}
                style={
                  c.repeat_contact
                    ? { backgroundColor: "rgba(253, 126, 20, 0.12)" }
                    : undefined
                }
              >
                <td style={{ maxWidth: 100 }}>
                  <Text size="xs" lineClamp={2}>
                    {c.visitor_name || "—"}
                  </Text>
                </td>
                <td style={{ maxWidth: 140 }}>
                  <Text size="xs" lineClamp={1}>
                    {c.visitor_email || "—"}
                  </Text>
                </td>
                <td style={{ maxWidth: 200 }}>
                  <Text size="sm" lineClamp={2}>
                    {c.query_message || c.user_query}
                  </Text>
                </td>
                <td>
                  <Text size="xs" lineClamp={1}>
                    {c.session_id || "—"}
                  </Text>
                </td>
                <td>
                  <Badge color={c.repeat_contact ? "orange" : "gray"}>
                    {c.repeat_contact ? "Repeat" : "Once"}
                  </Badge>
                </td>
                <td>{c.contact_count ?? 1}</td>
                <td>
                  <Text size="xs" color="dimmed">
                    {c.query_timestamp
                      ? new Date(c.query_timestamp).toLocaleString()
                      : "—"}
                  </Text>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7}>
                  <Text color="dimmed" align="center">
                    No fallback contacts recorded
                  </Text>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </ScrollArea>
    </Paper>
  );
}

function CleaningTab({ request, tenantId }) {
  const [status, setStatus] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingJobId, setUpdatingJobId] = useState(null);
  const [noteJob, setNoteJob] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const jobsListPath = useCallback(() => {
    const qs = new URLSearchParams({ tenant_id: tenantId, limit: "100" });
    if (status) qs.set("status", status);
    return `/api/cleaning/jobs?${qs.toString()}`;
  }, [tenantId, status]);

  const refreshJobsQuiet = useCallback(async () => {
    const data = await request(jobsListPath());
    setJobs(data.jobs || []);
  }, [request, jobsListPath]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    refreshJobsQuiet()
      .catch((e) => setError(humanError(e)))
      .finally(() => setLoading(false));
  }, [refreshJobsQuiet]);

  useEffect(() => {
    load();
  }, [load]);

  const onRowStatusChange = useCallback(
    async (job, nextStatus) => {
      if (!nextStatus || normalizeCleaningRowStatus(job.status) === nextStatus) return;
      setUpdatingJobId(job.id);
      try {
        await request(`/api/cleaning/jobs/${encodeURIComponent(job.id)}`, {
          method: "PUT",
          body: { status: nextStatus },
        });
        showNotification({
          title: "Status updated",
          message: `Job marked as ${nextStatus}.`,
          color: "teal",
        });
        await refreshJobsQuiet();
      } catch (e) {
        showNotification({
          title: "Could not update status",
          message: humanError(e),
          color: "red",
        });
      } finally {
        setUpdatingJobId(null);
      }
    },
    [request, refreshJobsQuiet]
  );

  const closeNoteModal = useCallback(() => {
    setNoteJob(null);
    setNoteDraft("");
  }, []);

  const saveNote = useCallback(async () => {
    if (!noteJob?.id) return;
    const st = normalizeCleaningRowStatus(noteJob.status);
    setSavingNote(true);
    try {
      await request(`/api/cleaning/jobs/${encodeURIComponent(noteJob.id)}`, {
        method: "PUT",
        body: { status: st, notes: noteDraft },
      });
      showNotification({
        title: "Notes saved",
        message: "Cleaning job notes were updated.",
        color: "teal",
      });
      closeNoteModal();
      await refreshJobsQuiet();
    } catch (e) {
      showNotification({
        title: "Could not save notes",
        message: humanError(e),
        color: "red",
      });
    } finally {
      setSavingNote(false);
    }
  }, [noteJob, noteDraft, request, refreshJobsQuiet, closeNoteModal]);

  return (
    <Paper shadow="sm" p="lg" radius="md" withBorder>
      {error && (
        <Alert color="red" mb="md">
          {error}
        </Alert>
      )}
      <Group mb="md">
        <Select
          label="Status"
          clearable
          placeholder="All"
          data={CLEANING_STATUS_SELECT_DATA}
          value={status}
          onChange={setStatus}
          style={{ maxWidth: 200 }}
        />
        <Button variant="light" onClick={load} mt={24}>
          Refresh
        </Button>
      </Group>
      {loading ? (
        <Center p="xl">
          <Loader />
        </Center>
      ) : (
        <ScrollArea h={520}>
          <Table striped>
            <thead>
              <tr>
                <th>Checkout</th>
                <th>Status</th>
                <th>Notes</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => {
                const rowStatus = normalizeCleaningRowStatus(j.status);
                const st = cleaningStatusDisplay(rowStatus);
                return (
                  <tr key={j.id}>
                    <td>{j.checkout_date || "—"}</td>
                    <td style={{ minWidth: 160, verticalAlign: "middle" }}>
                      <Select
                        size="xs"
                        data={CLEANING_STATUS_SELECT_DATA}
                        value={rowStatus}
                        disabled={updatingJobId === j.id}
                        onChange={(v) => onRowStatusChange(j, v)}
                        styles={{
                          input: {
                            fontWeight: 600,
                            ...(st.color === "yellow" && {
                              backgroundColor: "var(--mantine-color-yellow-1)",
                            }),
                            ...(st.color === "green" && {
                              backgroundColor: "var(--mantine-color-green-1)",
                            }),
                            ...(st.color === "red" && {
                              backgroundColor: "var(--mantine-color-red-1)",
                            }),
                          },
                        }}
                      />
                    </td>
                    <td style={{ maxWidth: 280, verticalAlign: "middle" }}>
                      <Group spacing="xs" noWrap align="flex-start">
                        <Text size="sm" lineClamp={2} style={{ flex: 1, minWidth: 0 }}>
                          {j.notes || "—"}
                        </Text>
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="gray"
                          aria-label="Edit notes"
                          onClick={() => {
                            setNoteJob(j);
                            setNoteDraft(j.notes ?? "");
                          }}
                        >
                          <IconPencil size={16} stroke={1.5} />
                        </ActionIcon>
                      </Group>
                    </td>
                    <td>
                      <Text size="xs">{j.updated_at || j.created_at || "—"}</Text>
                    </td>
                  </tr>
                );
              })}
              {jobs.length === 0 && (
                <tr>
                  <td colSpan={4}>
                    <Text color="dimmed" align="center">
                      No cleaning jobs
                    </Text>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </ScrollArea>
      )}
      <Modal opened={!!noteJob} onClose={closeNoteModal} title="Edit job notes" centered>
        <Stack spacing="sm">
          {noteJob?.checkout_date && (
            <Text size="sm" color="dimmed">
              Checkout: {noteJob.checkout_date}
            </Text>
          )}
          <Textarea
            label="Notes"
            placeholder="e.g. Cleaned by Maria at 14:00"
            minRows={4}
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.currentTarget.value)}
          />
          <Group position="right" mt="md">
            <Button variant="default" onClick={closeNoteModal}>
              Cancel
            </Button>
            <Button loading={savingNote} onClick={saveNote}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}

function syncFormFromConfig(c) {
  return {
    welcome_message: c.welcome_message ?? "",
    whatsapp_number: c.whatsapp_number ?? "",
    cleaning_whatsapp_number: c.cleaning_whatsapp_number ?? "",
    calendar_url: c.calendar_url ?? "",
    accommodation_name: c.accommodation_name ?? "",
    primary_color: c.primary_color ?? "#4a8078",
    fallback_message: c.fallback_message ?? "",
    locale: c.locale ?? "en",
    site_domain: c.site_domain ?? "",
    website_url: c.website_url ?? "",
  };
}

function SettingsTab({ request, tenantId }) {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(() => syncFormFromConfig({}));
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [ingestLoading, setIngestLoading] = useState(false);
  const [ingestMessage, setIngestMessage] = useState(null);
  const [ragStatus, setRagStatus] = useState(null);
  const [widgetInfo, setWidgetInfo] = useState(null);

  const loadRagStatus = useCallback(() => {
    request(`/api/rag/status/${encodeURIComponent(tenantId)}`)
      .then((s) => setRagStatus(s))
      .catch(() => setRagStatus(null));
  }, [request, tenantId]);

  const fetchConfig = useCallback(() => {
    setLoading(true);
    setError(null);
    request(`/api/config/${encodeURIComponent(tenantId)}`)
      .then((data) => {
        setConfig(data);
        setForm(syncFormFromConfig(data));
      })
      .catch((e) => setError(humanError(e)))
      .finally(() => setLoading(false));
  }, [request, tenantId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    loadRagStatus();
  }, [loadRagStatus]);

  useEffect(() => {
    request(`/api/widget/config/${encodeURIComponent(tenantId)}`, { skipAuth: true })
      .then((w) => setWidgetInfo(w))
      .catch(() => setWidgetInfo(null));
  }, [request, tenantId]);

  const triggerKnowledgeRefresh = () => {
    setIngestMessage(null);
    setIngestLoading(true);
    request("/api/rag/trigger-update", {
      method: "POST",
      body: { tenant_id: tenantId },
    })
      .then((r) => {
        setIngestMessage(
          r.message ||
            `Status: ${r.status || "ok"} — chunks: ${r.chunk_count ?? 0}, sources: ${r.source_count ?? 0}`
        );
        loadRagStatus();
      })
      .catch((e) => setIngestMessage(humanError(e)))
      .finally(() => setIngestLoading(false));
  };

  const handleEnabledToggle = (event) => {
    if (!config || toggling) return;
    const next = event.currentTarget.checked;
    const previous = config.enabled;
    const nid = "resibot-enabled";
    setConfig((c) => ({ ...c, enabled: next }));
    setToggling(true);
    showNotification({
      id: nid,
      loading: true,
      title: "Updating ResiBot…",
      message: "",
      autoClose: false,
      disallowClose: true,
    });
    request(`/api/config/${encodeURIComponent(tenantId)}`, {
      method: "PUT",
      body: { enabled: next },
    })
      .then((data) => {
        setConfig(data);
        updateNotification({
          id: nid,
          loading: false,
          title: "Saved",
          message: next ? "ResiBot is enabled for this tenant." : "ResiBot is disabled.",
          color: "teal",
          autoClose: 4000,
          disallowClose: false,
        });
      })
      .catch((e) => {
        setConfig((c) => ({ ...c, enabled: previous }));
        updateNotification({
          id: nid,
          loading: false,
          title: "Could not update",
          message: humanError(e),
          color: "red",
          autoClose: 8000,
          disallowClose: false,
        });
      })
      .finally(() => setToggling(false));
  };

  const handleSave = () => {
    if (!config) return;
    const body = {
      welcome_message: form.welcome_message,
      whatsapp_number: form.whatsapp_number,
      cleaning_whatsapp_number: form.cleaning_whatsapp_number,
      calendar_url: form.calendar_url,
      accommodation_name: form.accommodation_name,
      primary_color: form.primary_color,
      fallback_message: form.fallback_message,
      locale: form.locale,
      site_domain: form.site_domain || null,
      website_url: form.website_url || null,
    };
    const prevSnapshot = config;
    const optimistic = { ...config, ...body };
    setConfig(optimistic);
    const nid = "resibot-config-fields";
    setSaving(true);
    showNotification({
      id: nid,
      loading: true,
      title: "Saving settings…",
      message: "",
      autoClose: false,
      disallowClose: true,
    });
    request(`/api/config/${encodeURIComponent(tenantId)}`, {
      method: "PUT",
      body,
    })
      .then((data) => {
        setConfig(data);
        setForm(syncFormFromConfig(data));
        updateNotification({
          id: nid,
          loading: false,
          title: "Settings saved",
          message: "Your changes were saved.",
          color: "teal",
          autoClose: 4000,
          disallowClose: false,
        });
      })
      .catch((e) => {
        setConfig(prevSnapshot);
        setForm(syncFormFromConfig(prevSnapshot));
        updateNotification({
          id: nid,
          loading: false,
          title: "Save failed",
          message: humanError(e),
          color: "red",
          autoClose: 8000,
          disallowClose: false,
        });
      })
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }

  if (!config && error) {
    return (
      <Alert color="red" title="Configuration">
        {error}
      </Alert>
    );
  }

  if (!config) {
    return <Text color="dimmed">Unable to load configuration.</Text>;
  }

  return (
    <Grid>
      {error && (
        <Grid.Col span={12}>
          <Alert color="red" withCloseButton onClose={() => setError(null)}>
            {error}
          </Alert>
        </Grid.Col>
      )}
      <Grid.Col span={6}>
        <Paper shadow="sm" p="lg" radius="md" withBorder>
          <Title order={4} mb="md">
            Bot & widget
          </Title>
          <Group position="apart" mb="md">
            <div>
              <Text>ResiBot enabled</Text>
              <Text size="xs" color="dimmed">
                {config.enabled
                  ? "Guests can use the chat widget on your site when this is on."
                  : "Bot responses and the widget are off for this property."}
              </Text>
            </div>
            <Switch
              checked={config.enabled}
              onChange={handleEnabledToggle}
              disabled={toggling}
              size="lg"
              color="teal"
            />
          </Group>
          <Textarea
            label="Welcome message"
            minRows={3}
            value={form.welcome_message}
            onChange={(e) => setForm((f) => ({ ...f, welcome_message: e.target.value }))}
            mb="md"
          />
          <Textarea
            label="Fallback message"
            description="Shown when the bot escalates or cannot answer."
            minRows={2}
            value={form.fallback_message}
            onChange={(e) => setForm((f) => ({ ...f, fallback_message: e.target.value }))}
            mb="md"
          />
          <Select
            label="Locale"
            data={[
              { value: "en", label: "English" },
              { value: "nl", label: "Nederlands" },
              { value: "de", label: "Deutsch" },
            ]}
            value={form.locale}
            onChange={(v) => setForm((f) => ({ ...f, locale: v || "en" }))}
            mb="md"
          />

          <Title order={5} mt="lg" mb="sm">
            Integrations
          </Title>
          <TextInput
            label="WhatsApp number (guests)"
            description="Used when the bot offers a WhatsApp handoff to guests."
            placeholder="+41791234567"
            value={form.whatsapp_number}
            onChange={(e) => setForm((f) => ({ ...f, whatsapp_number: e.target.value }))}
            mb="md"
            icon={<IconBrandWhatsapp size={16} />}
          />
          <TextInput
            label="Cleaning team WhatsApp"
            description="Checkout and overdue-cleaning alerts go to this number (wa.me links in server logs unless Twilio is configured)."
            placeholder="+31612345678"
            value={form.cleaning_whatsapp_number}
            onChange={(e) => setForm((f) => ({ ...f, cleaning_whatsapp_number: e.target.value }))}
            mb="md"
            icon={<IconBrush size={16} />}
          />
          <TextInput
            label="Calendar URL (ICS)"
            description="Booking.com / Airbnb calendar feed for availability and cleaning automation."
            placeholder="https://ical.booking.com/v1/export?t=..."
            value={form.calendar_url}
            onChange={(e) => setForm((f) => ({ ...f, calendar_url: e.target.value }))}
            mb="md"
          />
          <TextInput
            label="Site domain"
            description="Your ResiWeb domain — used by pricing tool, cleaning agent, and RAG sync."
            placeholder="studiogocki.com"
            value={form.site_domain}
            onChange={(e) => setForm((f) => ({ ...f, site_domain: e.target.value }))}
            mb="md"
          />
          <TextInput
            label="Website URL"
            description="Full URL for live content scraping (Firecrawl). Leave blank if using RAG sync."
            placeholder="https://www.studiogocki.com"
            value={form.website_url}
            onChange={(e) => setForm((f) => ({ ...f, website_url: e.target.value }))}
            mb="md"
          />

          <Button color="teal" onClick={handleSave} loading={saving} disabled={toggling} mt="sm">
            Save settings
          </Button>
        </Paper>
      </Grid.Col>
      <Grid.Col span={6}>
        <Paper shadow="sm" p="lg" radius="md" withBorder>
          <Title order={4} mb="md">
            Branding
          </Title>
          <TextInput
            label="Property name"
            value={form.accommodation_name}
            onChange={(e) => setForm((f) => ({ ...f, accommodation_name: e.target.value }))}
            mb="md"
          />
          <Group align="flex-end" spacing="md" mb="md">
            <TextInput
              label="Primary color"
              value={form.primary_color}
              onChange={(e) => setForm((f) => ({ ...f, primary_color: e.target.value }))}
              style={{ flex: 1 }}
            />
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                backgroundColor: form.primary_color || "#4a8078",
                border: "1px solid #ddd",
                flexShrink: 0,
              }}
            />
          </Group>
          <Title order={5} mt="lg" mb="sm">
            Widget plugins
          </Title>
          <Text size="xs" color="dimmed" mb="xs">
            What your public site widget loads (read-only).
          </Text>
          <Group spacing="xs">
            {(widgetInfo?.enabled_plugins || []).length === 0 ? (
              <Text size="sm" color="dimmed">
                No plugins listed for this tenant.
              </Text>
            ) : (
              widgetInfo.enabled_plugins.map((p) => (
                <Badge key={p} variant="outline" color="teal">
                  {p}
                </Badge>
              ))
            )}
          </Group>

          {config.site_domain && (
            <>
              <Title order={5} mt="lg" mb="sm">
                Cleaning automation
              </Title>
              <Text size="xs" color="dimmed" mb="xs">
                The cleaning agent uses your ICS calendar to detect checkouts and targets the
                cleaning team WhatsApp number (or the guest number as fallback). Checkout time is read
                from your ResiRent dashboard settings.
              </Text>
              <Group spacing="xs">
                <Badge variant="light" color={config.calendar_url ? "teal" : "gray"}>
                  Calendar: {config.calendar_url ? "Connected" : "Not set"}
                </Badge>
                <Badge variant="light" color={config.cleaning_whatsapp_number || config.whatsapp_number ? "teal" : "gray"}>
                  Cleaning WhatsApp:{" "}
                  {config.cleaning_whatsapp_number
                    ? "Dedicated"
                    : config.whatsapp_number
                      ? "Using guest number (fallback)"
                      : "Not set"}
                </Badge>
              </Group>
            </>
          )}
        </Paper>
      </Grid.Col>
      <Grid.Col span={12}>
        <Paper shadow="sm" p="lg" radius="md" withBorder>
          <Group position="apart" mb="sm">
            <Title order={4}>
              Knowledge base (RAG)
            </Title>
            <Badge leftSection={<IconDatabase size={14} />} variant="light">
              Background sync
            </Badge>
          </Group>
          <Text size="sm" color="dimmed" mb="md">
            Rebuilds search content from your connected sources. The server must have data-source
            settings configured.
          </Text>
          <Group mb="md">
            <Button
              leftIcon={<IconDatabase size={18} />}
              loading={ingestLoading}
              onClick={triggerKnowledgeRefresh}
              color="teal"
            >
              Refresh knowledge base
            </Button>
            <Button variant="default" onClick={loadRagStatus}>
              Refresh status
            </Button>
          </Group>
          {ingestMessage && (
            <Alert color="blue" mb="sm" onClose={() => setIngestMessage(null)} withCloseButton>
              {ingestMessage}
            </Alert>
          )}
          {ragStatus && (
            <Text size="sm">
              <strong>Last job:</strong> {ragStatus.status || "—"} · chunks {ragStatus.chunk_count ?? 0} ·
              sources {ragStatus.source_count ?? 0}
              {ragStatus.error ? (
                <>
                  {" "}
                  <Badge color="red">Error</Badge> {ragStatus.error}
                </>
              ) : null}
            </Text>
          )}
        </Paper>
      </Grid.Col>
    </Grid>
  );
}

const ResiBotManager = () => {
  const { request, tenantId, setTenantId, allowedTenantIds } = useResibotApi();
  const envOverride = Boolean(
    process.env.REACT_APP_RESIBOT_TENANT_ID && String(process.env.REACT_APP_RESIBOT_TENANT_ID).trim()
  );

  if (!tenantId) {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Container size="xl" py="md">
      <Group position="apart" mb="md" align="center">
        <div>
          <Title order={2} style={{ letterSpacing: -0.5 }}>ResiBot Manager</Title>
          <Text size="sm" color="dimmed">Analytics & configuration for your chatbot</Text>
        </div>
        {allowedTenantIds.length > 1 && !envOverride ? (
          <Select
            label="Tenant workspace"
            placeholder="Select tenant"
            value={tenantId}
            onChange={(v) => v && setTenantId(v)}
            data={allowedTenantIds.map((t) => ({ value: t, label: t }))}
            w={220}
            size="sm"
          />
        ) : (
          <Badge size="lg" color="teal" variant="filled" radius="sm">
            {tenantId?.toUpperCase()}
          </Badge>
        )}
      </Group>

      <Tabs defaultValue="overview" variant="outline">
        <Tabs.List mb="lg">
          <Tabs.Tab value="overview" icon={<IconChartBar size={16} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="queries" icon={<IconList size={16} />}>
            Query log
          </Tabs.Tab>
          <Tabs.Tab value="faqs" icon={<IconHelp size={16} />}>
            FAQs
          </Tabs.Tab>
          <Tabs.Tab value="contacts" icon={<IconUsers size={16} />}>
            Contacts
          </Tabs.Tab>
          <Tabs.Tab value="cleaning" icon={<IconBrush size={16} />}>
            Cleaning
          </Tabs.Tab>
          <Tabs.Tab value="settings" icon={<IconSettings size={16} />}>
            Settings
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview">
          <OverviewTab request={request} />
        </Tabs.Panel>

        <Tabs.Panel value="queries">
          <QueriesTab request={request} tenantId={tenantId} />
        </Tabs.Panel>

        <Tabs.Panel value="faqs">
          <FaqsTab request={request} tenantId={tenantId} />
        </Tabs.Panel>

        <Tabs.Panel value="contacts">
          <ContactsTab request={request} tenantId={tenantId} />
        </Tabs.Panel>

        <Tabs.Panel value="cleaning">
          <CleaningTab request={request} tenantId={tenantId} />
        </Tabs.Panel>

        <Tabs.Panel value="settings">
          <SettingsTab request={request} tenantId={tenantId} />
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
};

export default ResiBotManager;
